import type { TableProps } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import React, { useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import styled from 'styled-components'
import { gray } from 'sutra/tokens/colors'

import { SutraSearch } from 'components/ui/SutraSearch'
import { SourceUrlKebab } from 'features/catalog/components/SourceUrlKebab/SourceUrlKebab'
import { StyledTable } from 'features/catalog/features/table/catalog-table.styles'
import type { FocusListOption } from 'features/catalog/types'
import { buildProjectCatalogPathFromLens } from 'features/catalog/utils'
// bully ignore[no-cross-feature-private-imports: the established panel-scoped source-group-info seam — this catalog panel already reached it transitively through SourceModal before this rewrite made the call direct, same shape as this file's other cross-feature hook imports elsewhere in catalog. follow-up: .agents/follow-ups/bully-typescript-cross-feature-hooks.md]
import { useSourceGroupInfo } from 'features/sourcepage/hooks/use-source-group-info'
import type { Source } from 'features/sourcepage/types'
import { usePageKeyboardShortcuts } from 'hooks/keyboard-shortcuts'
import { useTableKeyboardNavigation } from 'hooks/use-table-keyboard-navigation'

type Props = {
  item: FocusListOption
  projectId: string
}

function matchesSearch(source: Source, searchLower: string): boolean {
  return (
    source.title.toLowerCase().includes(searchLower) ||
    (source.source_url ?? '').toLowerCase().includes(searchLower)
  )
}

/**
 * The individual-sources list for a Sources-lens detail page. Replaces the
 * old `SourceDetail`/`SourceModal` widget — a plain `Input` + an antd
 * `Collapse` that mounted a `SourceTableDescription` + `SourceCustomFields`
 * subtree per row, eagerly, for every source in the group (the ~30s-load
 * culprit for a large group like "Okendo", hundreds of rows). This is a
 * flat, paginated table instead — the same `StyledTable` the catalog Sources
 * LIST view uses — with row click navigating to the source's underlying
 * product. `SourceModal`/`SourceCustomFields`/`SourceTableDescription`
 * themselves are untouched; their only other consumer is the unrouted
 * `Sourcepage`/`SourceCard` tree.
 */
export const CatalogSourceDetailPanel: React.FC<Props> = ({
  item,
  projectId,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchTerm, setSearchTerm] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)

  usePageKeyboardShortcuts(
    { '/': () => searchInputRef.current?.focus() },
    { respectInputs: true },
  )

  const sourcename = item.sourceName ?? item.value
  const country =
    item.country && item.country !== 'Unknown' ? item.country : undefined

  const { data, isLoading } = useSourceGroupInfo(
    sourcename,
    false,
    projectId,
    country,
  )

  const filteredSources = useMemo(() => {
    const sources = data?.sources ?? []
    if (!searchTerm) return sources
    const searchLower = searchTerm.toLowerCase()
    return sources.filter((source) => matchesSearch(source, searchLower))
  }, [data, searchTerm])

  // Carries the current `?filters=` (and any other search params) onto the
  // product detail page — a plain `navigate(path)` would otherwise drop the
  // query string, and cmd-click/middle-click open a fresh tab with no
  // filter state to overlay from. Shared by row click and Enter-to-activate.
  const buildProductPath = (source: Source): string | null => {
    const hierarchy = source.product?.hierarchy
    if (!hierarchy) return null
    return (
      buildProjectCatalogPathFromLens(
        projectId,
        'product_hierarchy',
        hierarchy,
      ) + location.search
    )
  }

  const {
    currentPage,
    pageSize,
    containerRef,
    onSearchKeyDown,
    onPageChange,
    onTableChange,
    getRowClassName,
  } = useTableKeyboardNavigation({
    rows: filteredSources,
    resetKey: searchTerm,
    pageSize: 25,
    onActivate: (source) => {
      const path = buildProductPath(source)
      if (path) navigate(path)
    },
  })

  const columns: ColumnsType<Source> = [
    {
      title: '#',
      key: 'number',
      width: 48,
      render: (_, __, index) => (
        <NumberCell>{(currentPage - 1) * pageSize + index + 1}</NumberCell>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Reviews',
      dataIndex: 'reviews',
      key: 'reviews',
      width: 120,
      align: 'right',
      sorter: (a, b) => a.reviews - b.reviews,
      render: (reviews: number) => reviews.toLocaleString(),
    },
    {
      title: '',
      key: 'actions',
      width: 48,
      align: 'center',
      render: (_, source) =>
        source.source_url ? (
          <SourceUrlKebab url={source.source_url} title={source.title} />
        ) : null,
    },
  ]

  const handleTableChange: NonNullable<TableProps<Source>['onChange']> = (
    ...args
  ) => {
    onTableChange(args[3].currentDataSource)
  }

  return (
    <PanelContainer ref={containerRef}>
      <SearchWrapper>
        <SutraSearch
          ref={searchInputRef}
          placeholder="Search for product"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          onKeyDown={onSearchKeyDown}
          shortcutHint="/"
        />
      </SearchWrapper>
      <StyledTable
        dataSource={filteredSources}
        columns={columns}
        rowKey="source_uuid"
        size="small"
        loading={isLoading}
        pagination={{
          current: currentPage,
          pageSize,
          showSizeChanger: true,
          onChange: onPageChange,
        }}
        onChange={handleTableChange}
        rowClassName={getRowClassName}
        onRow={(source) => {
          const path = buildProductPath(source)
          return {
            style: { cursor: path ? 'pointer' : 'default' },
            onClick: (event) => {
              if (!path) return
              if (event.metaKey || event.ctrlKey) {
                window.open(path, '_blank', 'noopener,noreferrer')
                return
              }
              navigate(path)
            },
            onMouseDown: (event) => {
              if (path && event.button === 1) {
                window.open(path, '_blank', 'noopener,noreferrer')
              }
            },
          }
        }}
      />
    </PanelContainer>
  )
}

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const SearchWrapper = styled.div`
  max-width: 400px;
`

const NumberCell = styled.span`
  color: ${gray[400]};
  font-variant-numeric: tabular-nums;
`
